import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toApiError } from '@/api/client';
import { GoldButton } from '@/components/common/GoldButton';
import { ProfileSupport } from '@/components/profile/ProfileSupport';
import { useAuth } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useToast } from '@/hooks/useToast';
import { t } from '@/i18n';
import { getNewPasswordError, newPasswordsMatch } from '@/utils/authValidation';
import styles from './ProfileMenu.module.css';
import formStyles from '../login/LoginForm.module.css';

export function ProfileMenu() {
  const navigate = useNavigate();
  const { user, logout, changePassword } = useAuth();
  const onApiError = useApiErrorToast();
  const { showToast } = useToast();

  const [changingPwd, setChangingPwd] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState({
    current: false,
    password: false,
    confirm: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const passwordError = getNewPasswordError(newPassword);
  const currentErr = touched.current && !currentPassword;
  const lengthErr = touched.password && passwordError !== null;
  const matchErr = touched.confirm && !newPasswordsMatch(newPassword, confirm);

  const resetForm = () => {
    // iOS Safari keeps an input's keyboard open through a tap on another
    // element unless focus is moved explicitly; closing it here (rather than
    // waiting for the input to unmount) starts its resize animation as early
    // as possible.
    (document.activeElement as HTMLElement | null)?.blur();
    setCurrentPassword('');
    setNewPassword('');
    setConfirm('');
    setTouched({ current: false, password: false, confirm: false });
    setChangingPwd(false);

    const scrollToAnchor = () =>
      anchorRef.current?.scrollIntoView({ block: 'nearest' });

    // Collapsing the form shrinks the page; without a correction the
    // viewport keeps its old scroll offset and can end up past the
    // now-shorter content. On iOS Safari the on-screen keyboard's dismiss
    // animation resizes the *visual* viewport asynchronously, so scrolling
    // immediately (a plain rAF) races it and gets overridden once it
    // finishes — wait for that resize to settle instead, with a timeout
    // fallback for when no keyboard was open (desktop, or already dismissed).
    const viewport = window.visualViewport;
    if (viewport) {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        viewport.removeEventListener('resize', finish);
        scrollToAnchor();
      };
      viewport.addEventListener('resize', finish);
      setTimeout(finish, 350);
    } else {
      requestAnimationFrame(scrollToAnchor);
    }
  };

  const submitChange = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ current: true, password: true, confirm: true });
    if (
      !currentPassword ||
      getNewPasswordError(newPassword) !== null ||
      !newPasswordsMatch(newPassword, confirm) ||
      submitting
    )
      return;
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      showToast(t.auth.changePassword.successToast, 'default');
      navigate('/login', { replace: true });
    } catch (err) {
      // A 401 means the session died, not that the change failed — let the
      // "session expired" default win instead of masking it with a custom
      // fallback (same fix as SpreadPage's job-creation error handling).
      onApiError(
        err,
        toApiError(err).status === 401
          ? undefined
          : t.auth.changePassword.failed,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      // Logout is best-effort; AuthContext clears local state even if the request fails.
    } finally {
      window.location.replace('/login');
    }
  };

  return (
    <div className={styles.list}>
      <ProfileSupport />

      {user?.hasPassword && (
        <div ref={anchorRef}>
          {changingPwd ? (
            <div
              className={styles.item}
              style={{
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 10,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {t.auth.changePassword.title}
              </div>
              <form
                onSubmit={submitChange}
                noValidate
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <input
                  className={[formStyles.inp, currentErr ? formStyles.err : '']
                    .filter(Boolean)
                    .join(' ')}
                  type="password"
                  placeholder={t.auth.changePassword.currentPlaceholder}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  onBlur={() =>
                    setTouched((value) => ({ ...value, current: true }))
                  }
                  disabled={submitting}
                  autoComplete="current-password"
                  required
                />
                {currentErr && (
                  <div
                    style={{ fontSize: 11, color: '#e53e3e', marginTop: -4 }}
                  >
                    {t.common.passwordRequired}
                  </div>
                )}
                <input
                  className={formStyles.inp}
                  type="password"
                  placeholder={t.auth.changePassword.newPlaceholder}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() =>
                    setTouched((value) => ({ ...value, password: true }))
                  }
                  disabled={submitting}
                  autoComplete="new-password"
                  required
                />
                {lengthErr && (
                  <div
                    style={{ fontSize: 11, color: '#e53e3e', marginTop: -4 }}
                  >
                    {passwordError === 'tooLong'
                      ? t.common.passwordMax128
                      : t.common.passwordMin8}
                  </div>
                )}
                <input
                  className={formStyles.inp}
                  type="password"
                  placeholder={t.auth.changePassword.confirmPlaceholder}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onBlur={() =>
                    setTouched((value) => ({ ...value, confirm: true }))
                  }
                  disabled={submitting}
                  autoComplete="new-password"
                  required
                />
                {matchErr && (
                  <div
                    style={{ fontSize: 11, color: '#e53e3e', marginTop: -4 }}
                  >
                    {t.common.passwordsDoNotMatch}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <GoldButton type="submit" disabled={submitting}>
                    {submitting ? t.common.saving : t.common.save}
                  </GoldButton>
                  <button
                    type="button"
                    className={styles.item}
                    style={{ flex: 1, padding: '10px 14px', fontSize: 13 }}
                    onClick={resetForm}
                    disabled={submitting}
                  >
                    {t.common.cancel}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <button
              type="button"
              className={styles.item}
              onClick={() => setChangingPwd(true)}
            >
              <span className={styles.left}>
                <span className={styles.ico} aria-hidden>
                  {String.fromCodePoint(0x1f511)}
                </span>
                {t.auth.changePassword.title}
              </span>
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className={styles.item}
        onClick={() => void handleLogout()}
        disabled={loggingOut}
      >
        <span className={styles.left}>
          <span className={styles.ico} aria-hidden>
            {String.fromCodePoint(0x1f6aa)}
          </span>
          {loggingOut ? t.auth.logoutSubmitting : t.auth.logout}
        </span>
      </button>
    </div>
  );
}
